/**
 * Исполнитель тура (E4.6b): открывает адрес шага в учебном экземпляре, ждёт готовности
 * страницы и цели без фиксированных задержек (загрузка раздела, запросы данных, изменения
 * разметки), засчитывает показанный шаг и завершённый урок, возвращает пользователя в точку
 * начала. Переход пользователя мимо тура приостанавливает тур на месте; перенаправление
 * охраной маршрута с адреса шага (страница закрыта для роли, roleRedirectFrom) делает шаг
 * недоступным, а не паузой.
 */

import { useIsFetching } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useWorkspaceDataSource } from '@/features/workspace/WorkspaceDataSourceContext';
import { roleRedirectFrom } from '@/router/roleRedirect';
import { LESSONS } from './catalog';
import { forgetMapState, restoreMapState } from './mapReturnState';
import { completeLesson, recordStep } from './progress';
import { findTrainingTarget, type TrainingTargetId } from './targets';
import { watchTarget } from './targetWatcher';
import { isTrainingHref, sameHref, samePath, tourStepHref } from './tourRoutes';
import { leaveTour, returnHandled, useTourStore } from './tourStore';
import { useTrainingProgress } from './useTrainingProgress';

/** Раздел ещё загружается (Suspense) */
function pageLoading(): boolean {
	return document.querySelector('[data-page-loading]') !== null;
}

function focusAfterReturn(target: string | null): void {
	const byTarget = target ? findTrainingTarget(target as TrainingTargetId) : null;
	const element =
		byTarget ??
		(target ? document.getElementById(target) : null) ??
		document.querySelector('main h1');
	if (!(element instanceof HTMLElement)) return;
	if (!element.matches('button, a, input, select, textarea, [tabindex]')) element.tabIndex = -1;
	element.focus({ preventScroll: true });
}

export function TrainingTourRuntime() {
	const { tour, manifest, returning, dispatch } = useTourStore();
	const location = useLocation();
	const navigate = useNavigate();
	const { mode, exitTraining } = useWorkspaceDataSource();
	const fetching = useIsFetching();
	const { update } = useTrainingProgress();
	const here = `${location.pathname}${location.search}`;
	const target =
		tour.lesson === null || manifest === null
			? null
			: tourStepHref(tour.lesson, tour.index, manifest);
	const step = tour.lesson === null ? null : LESSONS[tour.lesson].steps[tour.index];
	/* Адрес, где шаг показан (или объявлен недоступным): уход с него приостанавливает тур */
	const settledAt = useRef<string | null>(null);
	/* Охрана маршрута увела с адреса шага: страница шага закрыта для роли пользователя */
	const closedFor = roleRedirectFrom(location.state);
	const stepClosed = closedFor !== null && target !== null && samePath(closedFor, target);

	const settle = useCallback(
		(at: string, event: 'ready' | 'missing' | 'closed') => {
			settledAt.current = at;
			dispatch({ type: event });
		},
		[dispatch],
	);

	/* Шаг на другом адресе: переход (кроме закрытого для роли: туда не вернуться) */
	useEffect(() => {
		if (tour.phase !== 'preparing' || target === null || stepClosed || sameHref(here, target))
			return;
		navigate(target);
	}, [here, navigate, stepClosed, target, tour.phase]);

	/* Закрытая страница шага: шаг недоступен на адресе перенаправления -- после того, как
	   провайдер добавит к нему параметр учебного экземпляра */
	useEffect(() => {
		const settledClosed = tour.phase === 'unavailable' && tour.unavailable === 'page';
		const active =
			tour.phase === 'preparing' || tour.phase === 'showing' || tour.phase === 'unavailable';
		if (stepClosed && active && !settledClosed && isTrainingHref(here)) settle(here, 'closed');
	}, [here, settle, stepClosed, tour.phase, tour.unavailable]);

	/* Готовность цели: страница загружена, запросы завершены, цель на странице (targetWatcher) */
	useEffect(() => {
		if (tour.phase !== 'preparing' || target === null || step === null || !sameHref(here, target))
			return undefined;
		const watcher = watchTarget({
			find: () => findTrainingTarget(step) !== null,
			settled: () => !pageLoading() && fetching === 0,
			onReady: () => settle(here, 'ready'),
			onMissing: () => settle(here, 'missing'),
		});
		const observer = new MutationObserver(watcher.changed);
		observer.observe(document.body, { childList: true, subtree: true, attributes: true });
		return () => {
			observer.disconnect();
			watcher.stop();
		};
	}, [fetching, here, settle, step, target, tour.phase]);

	/* Показанный шаг засчитывается; пропущенный и недоступный -- нет */
	useEffect(() => {
		if (tour.phase === 'showing' && tour.lesson !== null) {
			const lesson = tour.lesson;
			update((progress) => recordStep(progress, lesson, tour.index));
		}
	}, [tour.index, tour.lesson, tour.phase, update]);

	/* Урок завершается, только если просмотрен каждый шаг */
	useEffect(() => {
		if (tour.phase === 'finished' && tour.lesson !== null) {
			const lesson = tour.lesson;
			update((progress) => completeLesson(progress, lesson).progress);
		}
	}, [tour.lesson, tour.phase, update]);

	/* Переход пользователя мимо тура (например, «Назад» браузера) приостанавливает тур;
	   перенаправление охраной с адреса шага -- не уход пользователя */
	useEffect(() => {
		if (
			!stepClosed &&
			(tour.phase === 'showing' || tour.phase === 'unavailable') &&
			settledAt.current !== null &&
			!sameHref(here, settledAt.current)
		) {
			leaveTour('pause', false);
		}
	}, [here, stepClosed, tour.phase]);

	/* Возврат: адрес и контур начала, затем прокрутка, карта и фокус на готовой странице */
	useEffect(() => {
		if (returning === null) return undefined;
		if (!sameHref(here, returning.href)) {
			if (mode !== 'live' && !isTrainingHref(returning.href)) exitTraining(returning.href);
			else navigate(returning.href);
			return undefined;
		}
		if (pageLoading() || fetching > 0) return undefined;
		if (returning.mapState) restoreMapState(returning.mapState);
		else forgetMapState();
		const frame = requestAnimationFrame(() => {
			window.scrollTo(returning.scroll.x, returning.scroll.y);
			focusAfterReturn(returning.focusTarget);
			returnHandled();
		});
		return () => cancelAnimationFrame(frame);
	}, [exitTraining, fetching, here, mode, navigate, returning]);

	return null;
}
